import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Play, FileText, HelpCircle, Check, Lock, ChevronRight, Award, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerCourseLearning() {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  const fetchCourse = useCallback(async () => {
    try {
      const res = await api.get(`/customer/courses/${courseId}`);
      setData(res.data);
      // Set first incomplete lesson as active
      if (!activeLesson) {
        for (const section of res.data.sections || []) {
          for (const lesson of section.lessons || []) {
            if (!lesson.completed) {
              setActiveLesson(lesson);
              return;
            }
          }
        }
        // All complete - set last lesson
        const lastSection = res.data.sections?.[res.data.sections.length - 1];
        const lastLesson = lastSection?.lessons?.[lastSection.lessons.length - 1];
        if (lastLesson) setActiveLesson(lastLesson);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [courseId, activeLesson]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  const handleComplete = async () => {
    if (!activeLesson) return;
    setCompleting(true);
    try {
      const res = await api.post(`/customer/courses/${courseId}/lessons/${activeLesson.id}/complete`);
      toast.success('Lesson selesai!');
      if (res.data.certificate) {
        toast.success('Selamat! Anda mendapat sertifikat! 🎉', { duration: 5000 });
      }
      fetchCourse();
      // Move to next lesson
      goToNextLesson();
    } catch (e) {
      toast.error('Gagal menyelesaikan lesson');
    } finally {
      setCompleting(false);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!activeLesson?.quiz) return;
    if (quizAnswers.length !== activeLesson.quiz.length) {
      toast.error('Jawab semua pertanyaan');
      return;
    }
    setSubmittingQuiz(true);
    try {
      const res = await api.post(`/customer/courses/${courseId}/lessons/${activeLesson.id}/quiz`, { answers: quizAnswers });
      setQuizResult(res.data);
      if (res.data.passed) {
        toast.success(`Quiz selesai! Skor: ${res.data.score}%`);
      } else {
        toast.error(`Skor: ${res.data.score}%. Coba lagi!`);
      }
    } catch (e) {
      toast.error('Gagal submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const goToNextLesson = () => {
    if (!data?.sections) return;
    let found = false;
    for (const section of data.sections) {
      for (const lesson of section.lessons || []) {
        if (found && !lesson.completed) {
          setActiveLesson(lesson);
          setQuizAnswers([]);
          setQuizResult(null);
          return;
        }
        if (lesson.id === activeLesson?.id) found = true;
      }
    }
  };

  const selectLesson = (lesson) => {
    setActiveLesson(lesson);
    setQuizAnswers([]);
    setQuizResult(null);
  };

  const getLessonIcon = (type, completed) => {
    if (completed) return <CheckCircle size={16} className="text-emerald-500" />;
    switch (type) {
      case 'video': return <Play size={16} className="text-blue-500" />;
      case 'text': return <FileText size={16} className="text-emerald-500" />;
      case 'quiz': return <HelpCircle size={16} className="text-amber-500" />;
      default: return <FileText size={16} />;
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 size={32} className="animate-spin text-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Lock size={48} className="mx-auto text-slate-300 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Akses Ditolak</h1>
        <Link to="/my/courses" className="text-[var(--store-primary, #2D6CDF)] hover:underline mt-2 inline-block">Kembali ke Kursus Saya</Link>
      </div>
    </div>
  );

  const course = data.course;
  const sections = data.sections || [];
  const progress = data.progress || {};
  const certificate = data.certificate;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="course-learning-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/my/courses" className="text-slate-500 hover:text-slate-900">
              <ArrowLeft size={20} />
            </Link>
            <div>
              <h1 className="font-semibold text-slate-900 line-clamp-1">{course.title}</h1>
              <div className="flex items-center gap-3 mt-1">
                <Progress value={progress.percent || 0} className="w-32 h-2" />
                <span className="text-xs text-slate-500">{progress.percent || 0}% selesai</span>
              </div>
            </div>
          </div>
          {certificate && (
            <Link to={`/my/certificates/${certificate.id}`}>
              <Button variant="outline" size="sm" className="text-amber-600 border-amber-200 bg-amber-50">
                <Award size={14} className="mr-1" /> Sertifikat
              </Button>
            </Link>
          )}
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-80 bg-white border-r border-slate-200 h-[calc(100vh-57px)] overflow-y-auto hidden lg:block">
          <div className="p-4">
            <h2 className="font-semibold text-slate-900 mb-4">Kurikulum</h2>
            <div className="space-y-3">
              {sections.map((section, sIdx) => (
                <div key={section.id}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">{sIdx + 1}</span>
                    <span className="text-sm font-medium text-slate-900">{section.title}</span>
                  </div>
                  <div className="ml-8 space-y-1">
                    {section.lessons?.map((lesson) => (
                      <button
                        key={lesson.id}
                        onClick={() => selectLesson(lesson)}
                        className={`w-full text-left p-2 rounded-lg flex items-center gap-2 transition-colors ${
                          activeLesson?.id === lesson.id ? 'bg-[var(--store-primary, #2D6CDF)]/10 text-[var(--store-primary, #2D6CDF)]' : 'hover:bg-slate-50 text-slate-600'
                        }`}
                        data-testid={`lesson-nav-${lesson.id}`}
                      >
                        {getLessonIcon(lesson.type, lesson.completed)}
                        <span className="text-sm flex-1 truncate">{lesson.title}</span>
                        {lesson.completed && <Check size={14} className="text-emerald-500" />}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {activeLesson ? (
            <div className="max-w-4xl mx-auto">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    {getLessonIcon(activeLesson.type, activeLesson.completed)}
                    <CardTitle className="text-xl">{activeLesson.title}</CardTitle>
                    {activeLesson.completed && <Badge className="bg-emerald-100 text-emerald-700">Selesai</Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Video Content */}
                  {activeLesson.type === 'video' && activeLesson.content_url && (
                    <div className="aspect-video bg-black rounded-lg overflow-hidden mb-6">
                      {activeLesson.content_url.includes('youtube') ? (
                        <iframe
                          src={activeLesson.content_url.replace('watch?v=', 'embed/')}
                          className="w-full h-full"
                          allowFullScreen
                          title={activeLesson.title}
                        />
                      ) : (
                        <video src={activeLesson.content_url} controls className="w-full h-full" />
                      )}
                    </div>
                  )}

                  {/* Text Content */}
                  {activeLesson.type === 'text' && activeLesson.content_text && (
                    <div className="prose prose-slate max-w-none mb-6">
                      <div className="whitespace-pre-wrap">{activeLesson.content_text}</div>
                    </div>
                  )}

                  {/* Quiz Content */}
                  {activeLesson.type === 'quiz' && activeLesson.quiz && (
                    <div className="space-y-6 mb-6">
                      {activeLesson.quiz.map((q, qIdx) => (
                        <div key={qIdx} className="p-4 bg-slate-50 rounded-lg">
                          <p className="font-medium text-slate-900 mb-3">{qIdx + 1}. {q.question}</p>
                          <RadioGroup
                            value={quizAnswers[qIdx]?.toString()}
                            onValueChange={(v) => {
                              const newAnswers = [...quizAnswers];
                              newAnswers[qIdx] = parseInt(v);
                              setQuizAnswers(newAnswers);
                            }}
                          >
                            {q.options.map((opt, oIdx) => (
                              <div key={oIdx} className="flex items-center space-x-2 py-1">
                                <RadioGroupItem value={oIdx.toString()} id={`q${qIdx}-o${oIdx}`} disabled={!!quizResult} />
                                <Label htmlFor={`q${qIdx}-o${oIdx}`} className={`cursor-pointer ${
                                  quizResult ? (
                                    q.correct_index === oIdx ? 'text-emerald-600 font-medium' :
                                    quizAnswers[qIdx] === oIdx ? 'text-red-600 line-through' : ''
                                  ) : ''
                                }`}>
                                  {opt}
                                  {quizResult && q.correct_index === oIdx && <CheckCircle size={14} className="inline ml-1 text-emerald-500" />}
                                  {quizResult && quizAnswers[qIdx] === oIdx && q.correct_index !== oIdx && <XCircle size={14} className="inline ml-1 text-red-500" />}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      ))}
                      {quizResult && (
                        <div className={`p-4 rounded-lg ${quizResult.passed ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                          <p className={`font-medium ${quizResult.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                            Skor: {quizResult.score}% ({quizResult.correct}/{quizResult.total} benar)
                          </p>
                          <p className="text-sm mt-1 text-slate-600">{quizResult.passed ? 'Selamat! Anda lulus quiz ini.' : 'Coba lagi untuk hasil lebih baik.'}</p>
                        </div>
                      )}
                      {!quizResult && (
                        <Button onClick={handleSubmitQuiz} disabled={submittingQuiz} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]" data-testid="submit-quiz-btn">
                          {submittingQuiz && <Loader2 size={14} className="animate-spin mr-2" />}
                          Submit Jawaban
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="text-sm text-slate-500">
                      {activeLesson.duration_minutes} menit
                    </div>
                    <div className="flex gap-2">
                      {!activeLesson.completed && activeLesson.type !== 'quiz' && (
                        <Button onClick={handleComplete} disabled={completing} className="bg-emerald-500 hover:bg-emerald-600" data-testid="complete-lesson-btn">
                          {completing && <Loader2 size={14} className="animate-spin mr-2" />}
                          <Check size={14} className="mr-1" /> Tandai Selesai
                        </Button>
                      )}
                      <Button onClick={goToNextLesson} variant="outline" data-testid="next-lesson-btn">
                        Lanjut <ChevronRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12">
              <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-900">Kursus Selesai!</h2>
              <p className="text-slate-500 mt-2">Anda telah menyelesaikan semua materi.</p>
              {certificate && (
                <Link to={`/my/certificates/${certificate.id}`}>
                  <Button className="mt-4 bg-amber-500 hover:bg-amber-600">
                    <Award size={16} className="mr-2" /> Lihat Sertifikat
                  </Button>
                </Link>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
